import React, { useEffect, useRef } from "react";
import { container } from "@/utils/mobx";
import { TextInputStore } from "./store";
import { VirtualKeyboard } from "@/components/toolkit/VirtualKeyboard";
import { CursorDisplay } from "@/components/toolkit/CursorDisplay";
import { isTouchDevice } from "@/utils/device";
import "./s.text_input.css";


// Resolved once at module load — touch capability doesn't change mid-session.
const IS_TOUCH = isTouchDevice();

// Approximate keyboard height (4 rows × 42px + 3 gaps × 6px + 16px padding).
// Used to pre-detect overlap before the keyboard DOM exists.
const VKB_HEIGHT = 210;

export const TextInput = container((props: {
    store: TextInputStore;
    value: string;
    onChange?: (value: string) => any;
    onEnter?: (value: string) => any;
    onFocus?: (e?: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => any;
    onBlur?: (e?: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => any;
    isTextArea?: boolean;
    submitOnEnter?: boolean;
    placeholder?: string;
    autoFocus?: boolean;
    className?: string;
    type?: string;
    disabled?: boolean;
    enableSplitter?: boolean;
    password?: boolean;
    selectOnFocus?: boolean;
}) => {
    const store = props.store;
    const className = "text_input " + (props.isTextArea ? "text_input--textarea " : "") + (props.className || "");

    // ── Mobile textarea with React virtual keyboard ──────────────────────────

    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const plainInputRef = useRef<HTMLInputElement>(null);
    const hiddenInputRef = useRef<HTMLInputElement>(null);
    const touchWrapperRef = useRef<HTMLDivElement>(null);

    // Re-evaluate showEmbedded when the VKB opens (using actual rendered position)
    // and on resize/orientation-change while the VKB is open.
    useEffect(() => {
        if (!IS_TOUCH || !store.vkbVisible) return;
        const recalc = () => {
            const inputEl = textareaRef.current ?? plainInputRef.current;
            if (!inputEl) return;
            const inputBottom = inputEl.getBoundingClientRect().bottom;
            const vkbEl = document.querySelector<HTMLElement>(".vkb");
            if (vkbEl) {
                store.toggleShowEmbedded(inputBottom > vkbEl.getBoundingClientRect().top);
            } else {
                store.toggleShowEmbedded(inputBottom > window.innerHeight - VKB_HEIGHT);
            }
        };
        // Re-check after the VKB renders so we use its actual position, not the approximation.
        requestAnimationFrame(recalc);
        window.addEventListener("resize", recalc);
        return () => window.removeEventListener("resize", recalc);
    }, [store.vkbVisible]);

    // Touch path focuses a hidden input, so the native focus/blur never reach
    // the parent. Surface them from virtual-keyboard visibility instead.
    const prevVkbVisibleRef = useRef(false);
    useEffect(() => {
        if (!IS_TOUCH) return;
        if (store.vkbVisible === prevVkbVisibleRef.current) return;
        prevVkbVisibleRef.current = store.vkbVisible;
        if (store.vkbVisible) props.onFocus?.();
        else props.onBlur?.();
    }, [store.vkbVisible]);

    // Reads the current DOM selection and saves it to the store.
    const saveCursor = () => {
        const el: HTMLTextAreaElement | HTMLInputElement | null = textareaRef.current ?? plainInputRef.current;
        if (!el) return;
        store.setSelectionRange(
            el.selectionStart ?? store.value.length,
            el.selectionEnd ?? store.value.length,
        );
    };

    if (IS_TOUCH) {
        // Show the cursor overlay when VKB is open and the embedded display is not shown.
        const showOverlay = store.vkbVisible && !store.vkbShowEmbedded;
        const touchClassName = className + (store.vkbVisible ? " text_input--vkb-active" : "");
        // Exclude the whole wrapper (input + cursor overlay) from the keyboard's
        // tap-outside-to-close — tapping the overlay repositions the cursor and
        // must keep the keyboard open.
        const excludeRef = touchWrapperRef as React.RefObject<Element>;

        const handlePointerDown = (e: React.PointerEvent) => {
            e.preventDefault();
            if (store.vkbVisible) {
                // Overlay or embedded display handles cursor repositioning.
                return;
            }
            if (store.selectOnFocus) {
                store.setSelectionRange(0, store.value.length);
            } else {
                store.setCursor(store.value.length);
            }
            const rect = (textareaRef.current ?? plainInputRef.current)?.getBoundingClientRect();
            store.openKeyboard(!!rect && rect.bottom > window.innerHeight - VKB_HEIGHT);
            hiddenInputRef.current?.focus();
        };

        // When the overlay is active it renders all text visually — the real
        // input must be empty so nothing shows through underneath.
        const displayValue = showOverlay ? "" : store.value;
        const displayPlaceholder = showOverlay ? "" : store.placeholder;

        const inputEl = props.isTextArea ? (
            <textarea
                ref={textareaRef}
                className={touchClassName}
                value={displayValue}
                placeholder={displayPlaceholder}
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck={false}
                inputMode="none"
                readOnly
                onPointerUp={handlePointerDown}
                onChange={() => {}}
            />
        ) : (
            <input
                ref={plainInputRef}
                type={store.password ? "password" : (props.type ?? "text")}
                className={touchClassName}
                value={displayValue}
                placeholder={displayPlaceholder}
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck={false}
                inputMode="none"
                readOnly
                onPointerUp={handlePointerDown}
                onChange={() => {}}
            />
        );

        return (
            <>
                <div ref={touchWrapperRef} className={"text_input__touch_wrapper" + (props.isTextArea ? " text_input__touch_wrapper--textarea" : "")}>
                    {inputEl}
                    {showOverlay && (
                        <CursorDisplay
                            className={"text_input__cursor_overlay" + (props.isTextArea ? " text_input__cursor_overlay--multiline" : "")}
                            value={store.value}
                            cursorPos={store.vkbCursorPos}
                            selStart={store.vkbSelStart}
                            selEnd={store.vkbSelEnd}
                            placeholder={store.placeholder}
                            onCursorMove={(pos) => store.setCursor(pos)}
                            onInsertText={(text) => store.insertText(text)}
                            onSelectAll={() => store.setSelectionRange(0, store.value.length)}
                            password={store.password}
                        />
                    )}
                </div>
                <input
                    ref={hiddenInputRef}
                    type="text"
                    inputMode="none"
                    aria-hidden="true"
                    tabIndex={-1}
                    className="text_input__hidden_input"
                    value=""
                    onChange={() => {}}
                />
                <VirtualKeyboard
                    store={store.vkbStore}
                    onKey={(key) => store.handleVkbKey(key)}
                    onClose={() => store.closeKeyboard()}
                    visible={store.vkbVisible}
                    value={store.value}
                    placeholder={store.placeholder}
                    cursorPos={store.vkbCursorPos}
                    selStart={store.vkbSelStart}
                    selEnd={store.vkbSelEnd}
                    onCursorMove={(pos) => store.setCursor(pos)}
                    onInsertText={(text) => store.insertText(text)}
                    onSelectAll={() => store.setSelectionRange(0, store.value.length)}
                    showEmbedded={store.vkbShowEmbedded}
                    excludeRef={excludeRef}
                    enableSplitter={props.enableSplitter}
                    password={store.password}
                />
            </>
        );
    }

    return props.isTextArea ? (
        <textarea
            className={className}
            value={store.value}
            placeholder={store.placeholder}
            autoFocus={store.autoFocus}
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck={false}
            onChange={(e) => store.updateValue(e.target.value)}
            onKeyDown={store.submitOnEnter ? (e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    store.onEnter?.(store.value);
                }
            } : undefined}
        />
    ) : (
        <input
            type={store.password ? "password" : (props.type ?? "text")}
            className={className}
            value={store.value}
            placeholder={store.placeholder}
            autoFocus={store.autoFocus}
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck={false}
            disabled={props.disabled}
            onChange={(e) => store.updateValue(e.target.value)}
            onMouseDown={props.selectOnFocus ? (e) => {
                if (document.activeElement !== e.currentTarget) {
                    e.preventDefault();
                    e.currentTarget.focus();
                    e.currentTarget.select();
                }
            } : undefined}
            onFocus={props.onFocus}
            onBlur={props.onBlur}
            onKeyDown={store.submitOnEnter ? (e) => {
                if (e.key === "Enter") {
                    e.preventDefault();
                    store.onEnter?.(store.value);
                }
            } : undefined}
        />
    );
}, TextInputStore);
